/**
 * Schema of public/game-data/terrain.json (written by tools/terrain/learn.ts) and of the per-style
 * user overrides saved by the tileset tool (asset kind 'terrain-style').
 *
 * Sprite choices are encoded as numbers: `sprite * 8 + flags` where sprite indexes TerrainStyleData.sprites
 * and flags are Unity tile flags (bit0 flipX, bit1 flipY, bit2 swap axes). Special choices: NONE / FILL.
 */
import type { DecorationSettings, FoliageKind, PaletteEntry, ShadingSettings } from '../model/format.ts';

/** no tile */
export const NONE = -1;
/** interior fill: resolved by the fill model (base fill pattern or dark shading tones) */
export const FILL = -2;

export type RuleLayer = 'level' | 'fg';
export type StampLayer = 'level' | 'fg' | 'bg';

export interface Directions {
  s45: boolean;
  s21: boolean;
  s12: boolean;
}

/**
 * Slopes every style can draw unless its override turns some off. A style's learned `directions` only
 * say which slopes its art has pieces for; cells on the others are reported as missing tiles.
 */
export const ALL_DIRECTIONS: Directions = { s45: true, s21: true, s12: true };

export interface RuleSet {
  /**
   * Context key (see keys.ts: 5 fallback levels from exact 8-neighbourhood to own shape only) ->
   * flat [choice, weight, choice, weight, ...].
   */
  rules: Record<string, number[]>;
  /** horizontal neighbour statistics: flat [leftChoice, rightChoice, count, ...] */
  bigramH: number[];
  /** vertical neighbour statistics: flat [belowChoice, aboveChoice, count, ...] */
  bigramV: number[];
  /** marginal counts per choice: flat [choice, count, ...] */
  marginal: number[];
  /**
   * Edge art at the end of a run (bare surface of the same side next to it): level-3 context key + ':'
   * + side of the bare neighbour (L, R, B, T) -> flat [choice, count, ...]
   */
  ends?: Record<string, number[]>;
}

export interface ToneTile {
  /** choice (sprite*8+flags) */
  c: number;
  /** usage weight */
  w: number;
  /** 4x4 darkness grid (row 0 = bottom), 0 = base tone, 1 = dark tone */
  g: number[];
  /** shape class of the tile's dark part (EMPTY = light tile, FULL = dark tile) */
  s: number;
}

export interface Tones {
  /** mean luminance of the base and the dark tone */
  base: number;
  dark: number;
  tiles: ToneTile[];
  /**
   * Context key of the dark region (own dark shape + neighbour dark shapes, keys.ts) -> flat
   * [choice, weight, ...], learned from the hand-shaded levels.
   */
  rules: Record<string, number[]>;
  /** slope kinds the dark region boundary may use */
  directions: Directions;
}

export interface Stamp {
  layer: StampLayer;
  /** [dx, dy, choice, ...] relative to the anchor cell */
  tiles: number[];
  /**
   * Terrain requirements [dx, dy, shape, mode, ...]; mode 0: cell shape must equal `shape`,
   * 1: cell must be empty, 2: cell must equal `shape` (support cell outside the stamp).
   */
  req: number[];
  /** probability per eligible anchor at density 1 */
  rate: number;
  /** occurrences in the learned levels */
  n: number;
  /** cells the stamp's sprites visually cover (for overlap tests): [dx, dy, ...] */
  cover: number[];
  /** never placed: disabled in the Tileset tool, or an arrow sign (tools/terrain/foliage.ts) */
  off?: boolean;
  /** foliage kind (tools/terrain/foliage.ts); missing: props */
  kind?: FoliageKind;
}

export interface TerrainStyleData {
  id: string;
  name: string;
  /** main texture guid */
  tex: string;
  /** sprite table referenced by choices */
  sprites: PaletteEntry[];
  /** learned shape class per sprite (-1 = not a terrain cell sprite) */
  shapes: number[];
  directions: Directions;
  /** colour variants: name + sprite remap (canonical sprite index -> variant sprite index). variants[0] is the canonical set. */
  variants: { name: string; map: Record<string, number> }[];
  level: RuleSet;
  fg: RuleSet;
  /** base interior fill: flat [choice, weight, ...] (neighbour statistics come from level.bigramH/V) */
  fill: number[];
  /** dark interior tones (absent when the art has none) */
  tones?: Tones;
  stamps: Stamp[];
  /** representative sprite for the style picker */
  preview: number;
  /** depth of the plain ground's edge band in cells (1/8 .. 1), for the dirt tile cases */
  edgeDepth?: number;
  defaultShading: ShadingSettings;
  defaultDecoration: DecorationSettings;
  /** learning statistics for the UI */
  stats?: { levels: string[]; cells: number };
}

export interface TerrainData {
  version: 1;
  /** sample resolution used for rasterizing (samples per cell edge) */
  samples: number;
  styles: TerrainStyleData[];
}

/** User edits stored with saveAsset('terrain-style', id). Absent fields keep the learned data. */
export interface StyleOverride {
  name?: string;
  directions?: Directions;
  /** extra sprites appended to the sprite table (indices continue after the learned table) */
  extraSprites?: PaletteEntry[];
  /** replaced rule choice lists */
  rules?: { level?: Record<string, number[]>; fg?: Record<string, number[]>; tones?: Record<string, number[]> };
  fill?: number[];
  /** tone tile weight overrides by index into tones.tiles (0 disables) */
  toneWeights?: Record<string, number>;
  /** stamp overrides by index: rate and/or disabled */
  stamps?: Record<string, { rate?: number; off?: boolean; kind?: FoliageKind }>;
  defaultShading?: ShadingSettings;
  defaultDecoration?: DecorationSettings;
  /** hand-linked tile cases (see cases.ts) */
  cases?: CaseTables;
  /** inside-corner spike tiles drawn in the pixel editor (dest cell shared by two painted sides) */
  spikeCorners?: Partial<Record<SpikeCorner, PaletteEntry>>;
}

/** Inside-corner spike of a dest cell: which quadrant of the empty cell the solid occupies. */
export type SpikeCorner = 'BL' | 'BR' | 'TL' | 'TR';

/**
 * Sprites linked to tile cases by hand: case key (cases.ts) -> flat [choice, weight, ...]. A case
 * without an entry uses the learned rules (or, with `mirror`, its mirror image's entry flipped).
 */
export interface CaseTables {
  dirt?: Record<string, number[]>;
  grass?: Record<string, number[]>;
  /** only linked cases: no learned pieces (unlinked dirt shows plain fill, unlinked grass nothing) */
  manualOnly?: boolean;
  /** unlinked cases use their mirror image's link, flipped */
  mirror?: boolean;
  /** drawing templates handed out, to link a drawn sheet back to its cases */
  templates?: CaseTemplate[];
}

export interface CaseTemplate {
  id: string;
  layer: 'dirt' | 'grass';
  /** cases in sheet order, row by row from the top-left */
  keys: string[];
  cols: number;
  /** tile size in pixels */
  tile?: number;
  created: string;
}
